import { useEffect } from 'react';
import { generatePath, Link, useLocation, useParams } from 'react-router';

import { AppRoutes } from '@/constants';
import { navigate } from '@/providers';

import { ChevronLeftIcon, EditIcon } from '@/assets/icons';
import {
  Badge,
  Button,
  Loading,
  SvgIcon,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/common';

import { getProfileStatusLabel, getProfileStatusVariant } from '@/features/face-id-profiles/utils';
import { useIpadConfigQuery } from '@/features/ipad-configs/hooks';
import {
  getAccessControlModeIcon,
  getAccessControlModeLabel,
  getAccessControlModeVariant,
  getDeviceTypeLabel,
  getDeviceTypeVariant,
} from '@/features/ipad-configs/utils';
import { AccessControlMode, DeviceType } from '@/features/ipad-configs/enums';

const IpadConfigDetails = () => {
  const { id } = useParams();
  const location = useLocation();
  const { data, isFetching, error } = useIpadConfigQuery(id || '');

  useEffect(() => {
    if (error || (!isFetching && !data)) {
      navigate(AppRoutes.NOT_FOUND, { replace: true });
    }
  }, [error, isFetching, data]);

  return (
    <div className="flex flex-col gap-base mt-xl mb-24 text-brand-primary-darkest">
      {/* Back Button */}
      <Button
        variant="link"
        className="text-neutral-n7 self-start"
        prefixIcon={<ChevronLeftIcon />}
        onClick={() => {
          const hasHistory = location.key !== 'default';
          if (hasHistory) {
            window.history.back();
          } else {
            navigate(AppRoutes.IPAD_CONFIGURATIONS);
          }
        }}
      >
        Back to iPad Configurations
      </Button>

      {/* Header with Title and Edit Button */}
      <div className="flex items-center justify-between gap-4 pb-base-plus">
        <h1
          className="text-h1 font-bold text-ellipsis flex-1 overflow-hidden whitespace-nowrap"
          title={data?.serialNumber}
        >
          {data?.serialNumber}
        </h1>
        <Button
          className="w-[104px]"
          variant="primary"
          borderRadius="soft"
          prefixIcon={<EditIcon />}
          onClick={() =>
            navigate(
              generatePath(AppRoutes.IPAD_CONFIGURATION_UPDATE, {
                id: data?.serialNumber,
              }),
            )
          }
        >
          Edit
        </Button>
      </div>

      {/* Details Card */}
      {isFetching && <Loading fullScreen />}
      {data && (
        <div className="rounded-softer border border-neutral-n4 bg-neutral-white">
          <div className="text-sm flex flex-col gap-y-base-plus p-m">
            <h6 className="text-brand-primary-darkest font-bold text-lg">Device Details</h6>
            {/* iPad Serial Number */}
            <div className="flex gap-x-xs">
              <div className="font-semibold text-brand-primary-darkest w-[120px] sm:w-[250px]">
                iPad Serial Number:
              </div>
              <div className="font-medium text-neutral-n8 flex-1 break-all">
                {data?.serialNumber}
              </div>
            </div>

            {/* Device Name */}
            <div className="flex gap-x-xs">
              <div className="font-semibold text-brand-primary-darkest w-[120px] sm:w-[250px]">
                Device Name:
              </div>
              <div className="font-medium text-neutral-n8 flex-1 break-all">{data?.deviceName}</div>
            </div>

            {/* Device Type */}
            <div className="flex gap-x-xs items-center">
              <div className="font-semibold text-brand-primary-darkest w-[120px] sm:w-[250px]">
                Device Type:
              </div>
              <Badge className="border-none" variant={getDeviceTypeVariant(data?.deviceType)}>
                {getDeviceTypeLabel(data?.deviceType)}
              </Badge>
            </div>
            {data?.deviceType === DeviceType.ProjectRoom && (
              <div className="text-sm flex flex-col gap-y-base-plus">
                <div className="h-px w-full bg-neutral-n3 my-1" />
                <h6 className="text-brand-primary-darkest font-bold text-lg">Project Details</h6>
                <div className="flex gap-x-xs">
                  <div className="font-semibold text-brand-primary-darkest w-[120px] sm:w-[250px]">
                    Project Name:
                  </div>
                  <div className="font-medium text-neutral-n8 flex-1 break-all">
                    {data?.projectName}
                  </div>
                </div>

                <div className="flex gap-x-xs items-center">
                  <div className="font-semibold text-brand-primary-darkest w-[120px] sm:w-[250px]">
                    Access Control Mode:
                  </div>
                  <div className="font-medium text-neutral-n8 flex-1 break-all">
                    <Badge variant={getAccessControlModeVariant(data?.accessControlMode)}>
                      <SvgIcon
                        component={getAccessControlModeIcon(data?.accessControlMode)}
                        className="size-4"
                      />
                      {getAccessControlModeLabel(data?.accessControlMode)}
                    </Badge>
                  </div>
                </div>

                {data?.accessControlMode === AccessControlMode.RestrictedAccess && (
                  <div className="flex gap-x-xs items-start">
                    <div className="font-semibold text-brand-primary-darkest w-[120px] sm:w-[250px]">
                      Allowed Members:
                    </div>
                    <Table className="!min-w-[650px] !w-[40%] !rounded-lg">
                      <TableHeader>
                        <TableRow>
                          <TableHead className="!w-[150px]">Face Profile ID</TableHead>
                          <TableHead className="!w-[300px]">Full Name</TableHead>
                          <TableHead className="!w-[200px]">Profile Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {data?.members?.map((member) => (
                          <TableRow key={member.profileId} rowId={member.profileId}>
                            <TableCell
                              align="left"
                              className="font-medium underline text-brand-primary-base !w-[150px]"
                            >
                              <Link
                                to={generatePath(AppRoutes.FACE_ID_PROFILE_DETAILS, {
                                  id: member.profileId,
                                })}
                                target="_blank"
                              >
                                {member.profileId}
                              </Link>
                            </TableCell>
                            <TableCell align="left" className="font-medium !w-[300px]">
                              <span className="block truncate" title={member.fullName}>
                                {member.fullName ? member.fullName : 'N/A'}
                              </span>
                            </TableCell>
                            <TableCell
                              align="left"
                              cellType="tag"
                              tagVariant={getProfileStatusVariant(member.status)}
                              className="!w-[200px]"
                            >
                              {getProfileStatusLabel(member.status)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
                <div className="h-px w-full bg-neutral-n3 my-1" />
              </div>
            )}

            {/* Remark */}
            <div className="flex gap-x-xs">
              <div className="font-semibold text-brand-primary-darkest w-[120px] sm:w-[250px]">
                Remark:
              </div>
              <div className="font-medium text-neutral-n8 flex-1 break-all">
                {data?.remarks ? data.remarks : <span className="tracking-[1px]">--</span>}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default IpadConfigDetails;
